/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import {
    CacheType,
    createObservableDataAction,
    IAction,
    IActionContext,
    IActionInput,
    ICreateActionContext
} from '@msdyn365-commerce/core';
import { getCartState } from '@msdyn365-commerce/global-state';
//mmx-feature: imports --start
/*
import { CartLine, QueryResultSettings } from '@msdyn365-commerce/retail-proxy';
*/
import { Address, CartLine, OrgUnitLocation, QueryResultSettings } from '@msdyn365-commerce/retail-proxy';
//mmx-feature: imports --end
import { addCartLinesAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/CartsDataActions.g';

//mmx-feature: imports --start
/*
import { getPaginatedOrderTemplateLinesAction, GetPaginatedOrderTemplateLinesInput } from './get-paginated-order-template-lines';
*/
import { getPaginatedOrderTemplateLinesAction, GetPaginatedOrderTemplateLinesInput } from '@msdyn365-commerce-modules/retail-actions';
//mmx-feature: imports --end

/**
 *  Input class for the AddOrderTemplateToCart data action.
 */
//mmx-feature: name change --start
/*
export class AddOrderTemplateToCartInput implements IActionInput {
*/
export class AddPickupOrderTemplateToCartInput implements IActionInput {
    //mmx-feature: name change --end
    //mmx-feature: order template pick up --start
    /*
    public readonly orderTemplateId: string;

    constructor(orderTemplateId: string) {
        this.orderTemplateId = orderTemplateId;
    }
    */
    public readonly orderTemplateId: string;
    public readonly locationData?: { orgUnitLocation: OrgUnitLocation; deliveryMode: string };

    constructor(orderTemplateId: string, locationData?: { orgUnitLocation: OrgUnitLocation; deliveryMode: string }) {
        this.orderTemplateId = orderTemplateId;
        this.locationData = locationData;
    }
    //mmx-feature: order template pick up --end

    public getCacheKey = () => 'AddOrderTemplateToCartInput';

    public getCacheObjectType = () => 'AddOrderTemplateToCartInput';

    public dataCacheType = (): CacheType => 'none';
}

/**
 * CreateInput method for the AddOrderTemplateToCart method.
 * @param {ICreateActionContext} inputData The input data passed to the createInput method.
 * @param {string} orderTemplateId The id of template.
 */
//mmx-feature: name change --start
/*
export const addOrderTemplateToCartInput = (inputData: ICreateActionContext, orderTemplateId: string): AddOrderTemplateToCartInput => {
*/
export const addPickupOrderTemplateToCartInput = (inputData: ICreateActionContext, orderTemplateId: string): AddPickupOrderTemplateToCartInput => {
    //mmx-feature: name change --end
    const { requestContext } = inputData;
    if (!requestContext.user.isAuthenticated) {
        throw new Error('Unable to create order template. User is not authenticated.');
    }
    //mmx-feature: name change --start
    /*
    return new AddOrderTemplateToCartInput(orderTemplateId);
    */
    return new AddPickupOrderTemplateToCartInput(orderTemplateId);
    //mmx-feature: name change --end
};

/**
 * The action method for the AddOrderTemplateToCart data action.
 * @param {AddOrderTemplateToCartInput} input The action input.
 * @param {IActionContext} ctx The action context.
 * @param context
 */
//mmx-feature: name change --start
/*
export async function addOrderTemplateToCartAction(input: AddOrderTemplateToCartInput, context: IActionContext): Promise<CartLine[]> {
*/
export async function addPickupOrderTemplateToCartAction(input: AddPickupOrderTemplateToCartInput, context: IActionContext): Promise<CartLine[]> {
    //mmx-feature: name change --end
    const cartState = await getCartState(context);
    const chunkCount = 1000;
    const queryResultSettings: QueryResultSettings = {
        Paging: {
            Top: chunkCount,
            Skip: 0
        }
    };
    const getPaginatedOrderTemplateLinesInput = new GetPaginatedOrderTemplateLinesInput(input.orderTemplateId, queryResultSettings);
    let orderTemplateLines = await getPaginatedOrderTemplateLinesAction(getPaginatedOrderTemplateLinesInput, context);
    let addedCartLines: CartLine[] = [];
    for (let skip = chunkCount; orderTemplateLines.lines.length > 0; skip += chunkCount) {
        const cartLines: CartLine[] = orderTemplateLines.lines.map(productLine => {
            const cartLine: CartLine = {
                ItemId: productLine.fullProduct?.ProductDetails.ItemId,
                ProductId: productLine.productListLine.ProductId,
                Quantity: productLine.productListLine.Quantity,
                TrackingId: '',
                UnitOfMeasureSymbol: productLine.productListLine.UnitOfMeasure
            };
            //mmx-feature: order template pick up --start
            if (input.locationData) {
                cartLine.DeliveryMode = input.locationData.deliveryMode ?? context.requestContext.channel?.PickupDeliveryModeCode ?? 'PICK';
                cartLine.FulfillmentStoreId = input.locationData.orgUnitLocation.OrgUnitNumber;
                cartLine.WarehouseId = input.locationData.orgUnitLocation.InventoryLocationId;
                cartLine.ShippingAddress = buildAddressFromOrgUnitLocation(input.locationData.orgUnitLocation);
            }
            //mmx-feature order template pick up --end
            return cartLine;
        });
        await addCartLinesAsync({ callerContext: context }, cartState.cart.Id, cartLines, cartState.cart.Version).catch(error => {
            context.telemetry.trace(error.data?.ValidationResults);
            context.telemetry.trace('Unable to add order template to cart.');
            throw error.data?.ValidationResults;
        });
        addedCartLines = addedCartLines.concat(cartLines);
        getPaginatedOrderTemplateLinesInput.queryResultSettings.Paging!.Skip = skip;
        orderTemplateLines = await getPaginatedOrderTemplateLinesAction(getPaginatedOrderTemplateLinesInput, context);
    }
    await cartState.refreshCart({});
    return addedCartLines;
}

//mmx-feature: order template pick up --start
const buildAddressFromOrgUnitLocation = (location: OrgUnitLocation): Address => {
    return {
        RecordId: location.PostalAddressId,
        Name: location.OrgUnitName,
        FullAddress: location.Address,
        Street: location.Street,
        StreetNumber: location.StreetNumber,
        City: location.City,
        DistrictName: location.DistrictName,
        BuildingCompliment: location.BuildingCompliment,
        Postbox: location.Postbox,
        ThreeLetterISORegionName: location.Country,
        ZipCode: location.Zip,
        County: location.County,
        CountyName: location.CountyName,
        State: location.State,
        StateName: location.StateName
    };
};
//mmx-feature order template pick up --end

/**
 * The AddOrderTemplateToCart Data Action.
 * Returns created order template.
 */
//mmx-feature: name change --start
/*
export const addOrderTemplateToCartActionDataAction = createObservableDataAction<CartLine[]>({
    id: '@msdyn365-commerce-modules/retail-actions/order-templates/add-order-template-to-cart',
    action: <IAction<CartLine[]>>addOrderTemplateToCartAction,
    input: addOrderTemplateToCartInput
});
export default addOrderTemplateToCartActionDataAction;
*/
//mmx-feature: name change --end
export const addPickupOrderTemplateToCartActionDataAction = createObservableDataAction<CartLine[]>({
    id: '@msdyn365-commerce-modules/retail-actions/order-templates/add-order-template-to-cart',
    action: <IAction<CartLine[]>>addPickupOrderTemplateToCartAction,
    input: addPickupOrderTemplateToCartInput
});
export default addPickupOrderTemplateToCartActionDataAction;